import { supabase } from '../supabase';
import { Database } from '../../types/database';

type Expense = Database['public']['Tables']['expenses']['Row'];
type ExpenseInsert = Database['public']['Tables']['expenses']['Insert'];
type ExpenseUpdate = Database['public']['Tables']['expenses']['Update'];

export const expenseService = {
  /**
   * Get all expenses
   */
  getExpenses: async () => {
    const { data, error } = await supabase
      .from('expenses')
      .select('*')
      .order('date', { ascending: false });
    return { data, error };
  },

  /**
   * Get expenses by date range
   */
  getExpensesByDateRange: async (startDate: string, endDate: string) => {
    const { data, error } = await supabase
      .from('expenses')
      .select('*')
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: false });
    return { data, error };
  },

  /**
   * Get expenses by category
   */
  getExpensesByCategory: async (category: string) => {
    const { data, error } = await supabase
      .from('expenses')
      .select('*')
      .eq('category', category)
      .order('date', { ascending: false });
    return { data, error };
  },

  /**
   * Create a new expense
   */
  createExpense: async (expense: Omit<ExpenseInsert, 'id' | 'created_at' | 'updated_at'>) => {
    const { data, error } = await supabase
      .from('expenses')
      .insert(expense)
      .select()
      .single();
    return { data, error };
  },

  /**
   * Update an existing expense
   */
  updateExpense: async (id: string, updates: ExpenseUpdate) => {
    const { data, error } = await supabase
      .from('expenses')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  /**
   * Delete an expense
   */
  deleteExpense: async (id: string) => {
    const { data, error } = await supabase
      .from('expenses')
      .delete()
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  /**
   * Get expense categories
   */
  getExpenseCategories: async () => {
    const { data, error } = await supabase
      .from('expenses')
      .select('category')
      .order('category');
    
    if (error) return { data: [], error };
    
    // Get unique categories
    const categories = [...new Set(data.map(item => item.category))];
    return { data: categories, error: null };
  },

  /**
   * Get total expenses by category
   */
  getExpenseTotalsByCategory: async () => {
    const { data, error } = await supabase
      .rpc('get_expense_totals_by_category');
    return { data, error };
  }
};